import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { I18nProvider } from "@/i18n";
import { Toaster } from "@/components/ui/sonner";
import Layout from "@/components/Layout";
import SessionWatch from "@/components/SessionWatch";
import IdleLogout from "@/components/IdleLogout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Products from "@/pages/Products";
import Bundles from "@/pages/Bundles";
import Inventory from "@/pages/Inventory";
import Receiving from "@/pages/Receiving";
import Counts from "@/pages/Counts";
import Orders from "@/pages/Orders";
import OrderDetail from "@/pages/OrderDetail";
import PickPack from "@/pages/PickPack";
import Returns from "@/pages/Returns";
import SalesPromotionsPage from "@/pages/SalesPromotionsPage";
import B2B from "@/pages/B2B";
import Finance from "@/pages/Finance";
import ImportWizard from "@/pages/ImportWizard";
import InvoiceImport from "@/pages/InvoiceImport";
import SettingsPage from "@/pages/Settings";
import Invoice from "@/pages/Invoice";
import DeliveryNote from "@/pages/DeliveryNote";
import CN23 from "@/pages/CN23";
import Messe from "@/pages/Messe";
import MesseReport from "@/pages/MesseReport";
import InventurReport from "@/pages/InventurReport";
import Packaging from "@/pages/Packaging";
import OwnWithdrawals from "@/pages/OwnWithdrawals";
import OwnWithdrawalsReport from "@/pages/OwnWithdrawalsReport";
import Promotions from "@/pages/Promotions";
import PromotionReport from "@/pages/PromotionReport";
import Profile from "@/pages/Profile";
import Tenants from "@/pages/Tenants";

function Protected({ children }) {
  const { user } = useAuth();
  const loc = useLocation();
  if (user === undefined)
    return <div className="flex h-screen items-center justify-center text-muted-foreground">…</div>;
  if (!user) return <Navigate to="/login" state={{ from: loc }} replace />;
  // Route-Guard: der Disponent arbeitet ausschließlich im Lager-/Warenbereich. Die serverseitige
  // Rechteprüfung bleibt maßgeblich, die Weiterleitung verhindert nur irreführende Ansichten.
  if (user.role === "disponent" && !DISPONENT_ROUTES.some(
    (p) => loc.pathname === p || loc.pathname.startsWith(`${p}/`)))
    return <Navigate to="/" replace />;
  // Excel-Import: nur Mandant Gluty-Free Club (GFC) und die Betreiber-Ebene.
  if (loc.pathname.startsWith("/import") && user.tenant?.code !== "GFC"
      && user.tenant?.slug !== "gfc"
      && !["superadmin", "dev"].includes(user.platform_role))
    return <Navigate to="/" replace />;
  return <Layout>{children}</Layout>;
}

const DISPONENT_ROUTES = ["/", "/produkte", "/bundles", "/lager", "/wareneingang", "/verpackung",
  "/inventur", "/eigenentnahmen", "/promotion", "/auftraege", "/pick-pack", "/retouren", "/profil"];

const R = (el) => <Protected>{el}</Protected>;

function App() {
  return (
    <AuthProvider>
      <I18nProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <SessionWatch />
        <IdleLogout />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/" element={R(<Dashboard />)} />
          <Route path="/produkte" element={R(<Products />)} />
          <Route path="/bundles" element={R(<Bundles />)} />
          <Route path="/lager" element={R(<Inventory />)} />
          <Route path="/wareneingang" element={R(<Receiving />)} />
          <Route path="/inventur" element={R(<Counts />)} />
          <Route path="/verpackung" element={R(<Packaging />)} />
          <Route path="/eigenentnahmen" element={R(<OwnWithdrawals />)} />
          <Route path="/eigenentnahmen/report/:year" element={R(<OwnWithdrawalsReport />)} />
          <Route path="/promotion" element={R(<Promotions />)} />
          <Route path="/promotion/report/:year" element={R(<PromotionReport />)} />
          <Route path="/profil" element={R(<Profile />)} />
          <Route path="/mandanten" element={R(<Tenants />)} />
          <Route path="/auftraege" element={R(<Orders />)} />
          <Route path="/messe" element={R(<Messe />)} />
          <Route path="/messe/:id/report" element={R(<MesseReport />)} />
          <Route path="/inventur/:id/report" element={R(<InventurReport />)} />
          <Route path="/auftraege/:id" element={R(<OrderDetail />)} />
          <Route path="/pick-pack" element={R(<PickPack />)} />
          <Route path="/retouren" element={R(<Returns />)} />
          <Route path="/b2b" element={R(<B2B />)} />
          <Route path="/sales-aktionen" element={R(<SalesPromotionsPage />)} />
          <Route path="/finanzen" element={R(<Finance />)} />
          <Route path="/shopify" element={<Navigate to="/einstellungen?tab=integrations" replace />} />
          <Route path="/import" element={R(<ImportWizard />)} />
          <Route path="/rechnungsimport" element={R(<InvoiceImport />)} />
          <Route path="/einstellungen" element={R(<SettingsPage />)} />
          <Route path="/rechnung/:id" element={R(<Invoice />)} />
          <Route path="/lieferschein/:id" element={R(<DeliveryNote />)} />
          <Route path="/cn23/:id" element={R(<CN23 />)} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      </I18nProvider>
    </AuthProvider>
  );
}

export default App;
